import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import moment from 'src/app/utils/moment';
import { AppService, OrganizationService } from 'src/app/services';

export type ProgramDatePopoverSide = 'top' | 'bottom';

/** Calendar + time + actions is ~520px; prefer the side that can fit it. */
export function getProgramDatePopoverSide(event: Event): ProgramDatePopoverSide {
  const clientY = typeof (event as MouseEvent).clientY === 'number'
    ? (event as MouseEvent).clientY
    : window.innerHeight / 2;
  const margin = 16;
  const estimatedHeight = 520;
  const spaceBelow = window.innerHeight - clientY - margin;
  const spaceAbove = clientY - margin;
  if (spaceBelow >= estimatedHeight) {
    return 'bottom';
  }
  return spaceAbove > spaceBelow ? 'top' : 'bottom';
}

@Component({
  selector: 'app-program-date-popover',
  templateUrl: './program-date-popover.component.html',
  styleUrls: ['./program-date-popover.component.scss'],
})
export class ProgramDatePopoverComponent implements OnInit, AfterViewInit, OnDestroy {

  public toBind: string;
  public selected;
  public time: string | null = null;

  @Input() initial: string;
  @Input() clear = false;
  @Input() max: string;
  @Input() includeTime = false;
  @Input() dateType: "startDate" | "endDate" = "startDate";

  private resizeObserver: ResizeObserver | null = null;
  private lastFitKey = '';

  constructor(
    public appService: AppService,
    private organizationService: OrganizationService,
    private popoverCtrl: PopoverController,
    private elementRef: ElementRef<HTMLElement>,
  ) { }

  ngOnInit() {
    if(this.initial) {
      this.toBind = this.initial;
      this.selected = new Date(this.initial);
      if (this.includeTime) {
        this.time = (moment as any)(this.initial).format("HH") + ":00:00";
      }
    } else if (this.includeTime) {
      const programSettings = this.organizationService.getAppOrganization()?.getSetting("programs");
      this.time = this.dateType === "endDate"
        ? (programSettings?.defaultEndTime || null)
        : (programSettings?.defaultStartTime || null);
    }
  }

  ngAfterViewInit() {
    this.fitPopoverInViewport();
    requestAnimationFrame(() => this.fitPopoverInViewport());
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.fitPopoverInViewport());
      this.resizeObserver.observe(this.elementRef.nativeElement);
    }
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  onSelect(event: Date) {
    if(event) {
      this.selected = event;
      this.toBind = this.includeTime ? this.composeIso(event) : event.toISOString();
    }
  }

  async clearDate() {
    const popover = await this.popoverCtrl.getTop();
    if (popover) {
      await popover.dismiss(null, "cleared"); 
    }
  }

  async dismiss() {
    const popover = await this.popoverCtrl.getTop();
    if (popover) {
      const value = this.includeTime && this.selected ? this.composeIso(this.selected) : this.toBind;
      await popover.dismiss(value, "set"); 
    }
  }

  private composeIso(date: Date): string {
    const composed = (moment as any)(date);
    if (this.time) {
      const [hourStr] = this.time.split(":");
      const hour = parseInt(hourStr, 10);
      if (!Number.isNaN(hour)) {
        composed.hour(hour).minute(0).second(0).millisecond(0);
      }
    } else {
      composed.hour(0).minute(0).second(0).millisecond(0);
    }
    return composed.toDate().toISOString();
  }

  private fitPopoverInViewport() {
    const popoverEl = this.elementRef.nativeElement.closest('ion-popover');
    const content = popoverEl?.shadowRoot?.querySelector('.popover-content') as HTMLElement | null
      ?? popoverEl?.shadowRoot?.querySelector('[part="content"]') as HTMLElement | null;
    if (!content) {
      return;
    }

    const padding = 12;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const maxHeight = Math.max(240, viewportHeight - padding * 2);
    content.style.maxHeight = `${maxHeight}px`;

    const rect = content.getBoundingClientRect();
    let top = rect.top;
    let left = rect.left;

    if (rect.height > maxHeight) {
      top = padding;
    } else if (rect.bottom > viewportHeight - padding) {
      top = Math.max(padding, viewportHeight - padding - rect.height);
    } else if (rect.top < padding) {
      top = padding;
    }

    if (rect.right > viewportWidth - padding) {
      left = Math.max(padding, viewportWidth - padding - rect.width);
    } else if (rect.left < padding) {
      left = padding;
    }

    const fitKey = `${maxHeight}|${Math.round(top)}|${Math.round(left)}`;
    if (fitKey === this.lastFitKey) {
      return;
    }
    this.lastFitKey = fitKey;

    if (top !== rect.top || left !== rect.left) {
      content.style.top = `${top}px`;
      content.style.left = `${left}px`;
      content.style.transform = 'none';
    }
  }
}
